import XRegExp from "xregexp";
import { DateTime, Settings } from "luxon";
import { isEmpty, some, memoize, toUpper, uniq, map } from "../utils/lodash";
import { AnswerRecalledValue } from "./AnswerRecalledValue";
import { RecallType } from "./RecallType";
import { RecalledValue } from "./RecalledValue";
import { SystemRecalledValue } from "./SystemRecalledValue";
import { DateRecalledValue } from "./DateRecalledValue";
import { SurveyRecalledValue } from "./SurveyRecalledValue";
import { TimeRecalledValue } from "./TimeRecalledValue";
import { TimeSpanRecalledValue } from "./TimeSpanRecalledValue";
import { VariablesState } from "../models/states/VariablesState";
import { InterviewState } from "../models/states/InterviewState";
import { Log } from "../utils/logging";
import { QuestionType } from "../models/enums/QuestionType";
import { getSettingBoolean, getSettingString } from "../models/utilities/settingUtilities";
import { DateTimeRecalledValue } from "./DateTimeRecalledValue";
import { RespondentModel } from "../models/RespondentModel";
import { isNullOrEmpty } from "../utils";
import { getVariableRow } from "../redux/reducers/interview/navigationUtilities";
import { NavigationState } from "../models/states/NavigationState";
import {
    getQuestionWithVariable,
    getVariableWithName,
} from "../models/utilities/questionnaireUtilities";
import { QuestionnaireModel } from "../models/QuestionnaireModel";
import { dateFormat } from "../models/utilities/questionUtilities";
import { cDateFormatToLuxon } from "../utils/time";
import { getMentions } from "../redux/reducers/interview/variablesUtilities";
import { DayRecalledValue } from "./DayRecalledValue";
import { getCoordinates } from "../models/utilities/respondentUtilities";

const TAG = "piping";

const QUESTION_VARIABLE_NAME_REGEX_VALUE = "(?<name>[a-z0-9_]+)";
const SYSTEM_VARIABLE_NAME_REGEX_VALUE = "(?<name>\\$[a-z0-9_]+)";

const MENTION_REGEX_VALUE = "(\\(|%28)(?<mention>[a-z0-9-]+)(\\)|%29)";
const MATRIX_REGEX_VALUE = "(\\.Row)(\\(|%28)(?<matrix>[a-z0-9\\-]+)(\\)|%29)";
const FORMAT_REGEX_VALUE = "(\\.Format)(\\(|%28)(?<format>[a-zA-Z \\,\\:\\-\\/]+)(\\)|%29)";

const VARIABLE_PROPERTY = "\\.(?<property>[a-z]+)((\\(|%28)(?<param>[a-z0-9/:\\-]+)(\\)|%29))?";

const QUESTION_VARIABLE = XRegExp(
    `\\[${QUESTION_VARIABLE_NAME_REGEX_VALUE}(${MATRIX_REGEX_VALUE})?(${FORMAT_REGEX_VALUE})?(${MENTION_REGEX_VALUE})?(?<properties>(${VARIABLE_PROPERTY})*)\\]`,
    "in",
);
const SYSTEM_VARIABLE = XRegExp(
    `\\[${SYSTEM_VARIABLE_NAME_REGEX_VALUE}(${FORMAT_REGEX_VALUE})?(?<properties>(${VARIABLE_PROPERTY})*)\\]`,
    "in",
);

export class Piping {
    /**
     * Helper function to extract match object with groups from XRegExp.replace callback arguments.
     * In XRegExp v5.0, the match object may already have groups, or groups may be in the last argument.
     * Also handles cases where match might be a string that needs to be exec'd.
     */
    private static extractMatchWithGroups(args: any[], regex: RegExp): any {
        const matchObj = args[0];

        // If match object already has groups (XRegExp v5.0+), use it directly
        if (matchObj && typeof matchObj === "object" && matchObj.groups !== undefined) {
            return matchObj;
        }

        // Check if groups are in the last argument (alternative v5.0 pattern)
        const lastArg = args[args.length - 1];
        if (lastArg && typeof lastArg === "object" && lastArg.groups) {
            // Merge groups with match object
            if (typeof matchObj === "string") {
                return { 0: matchObj, groups: lastArg.groups };
            } else if (matchObj && typeof matchObj === "object") {
                return { ...matchObj, groups: lastArg.groups };
            }
        }

        // If match is a string, try to exec it to get proper match object with groups
        if (typeof matchObj === "string") {
            const execResult = XRegExp.exec(matchObj, regex);
            if (execResult) {
                return execResult;
            }
        }

        // If match object exists but doesn't have groups, try to exec the match string
        if (matchObj && typeof matchObj === "object" && matchObj[0]) {
            const matchString = matchObj[0];
            if (typeof matchString === "string") {
                const execResult = XRegExp.exec(matchString, regex);
                if (execResult && execResult.groups) {
                    return execResult;
                }
            }
        }

        return matchObj;
    }

    /**
     * Helper function to safely access named groups from XRegExp match objects.
     * Supports both XRegExp v4.x (direct properties) and v5.0+ (groups object) APIs.
     */
    private static getNamedGroup(match: any, groupName: string): any {
        if (!match) return undefined;
        // XRegExp v5.0+ uses match.groups.name
        if (match.groups !== undefined) {
            return match.groups[groupName];
        }
        // Fallback for v4.x compatibility (direct properties)
        return match[groupName];
    }

    private static getProperties(match: any) {
        const matches: any[] = [];
        const properties = this.getNamedGroup(match, "properties");
        if (properties) {
            XRegExp.forEach(properties, XRegExp(VARIABLE_PROPERTY, "gi"), (innerMatch: any) => {
                const property = this.getNamedGroup(innerMatch, "property");
                const param = this.getNamedGroup(innerMatch, "param");
                matches.push({ property, param });
            });
        }
        const mention = this.getNamedGroup(match, "mention");
        const format = this.getNamedGroup(match, "format");
        if (mention != null) matches.unshift({ property: "answer", param: [mention] });
        if (format != null) matches.unshift({ property: "format", param: [format] });
        if (matches.length === 0) matches.unshift({ property: "answer" });
        return matches;
    }

    private static getDefaultValue(match: any, mode: RecallType) {
        const property = this.getNamedGroup(match, "property");
        if (!isNullOrEmpty(property)) return property.toLowerCase() === "code" ? "0" : "";
        return mode === RecallType.Code ? "0" : "";
    }

    private static recallSystemMatch(
        match: any,
        variables: VariablesState,
        questionnaire: QuestionnaireModel,
        respondent?: RespondentModel,
    ): string | RecalledValue {
        const name = this.getNamedGroup(match, "name");
        if (!match || !name) {
            return "";
        }
        let variableName = name.toUpperCase();
        if (!variableName.startsWith("$")) variableName = `$${variableName}`;
        Settings.defaultLocale = respondent.language;
        const t = DateTime.utc();
        const tz = DateTime.local();
        switch (variableName) {
            case "$D":
                return new DateRecalledValue(tz);
            case "$H":
                return new TimeRecalledValue(tz);
            case "$UTCDATE":
                return new DateRecalledValue(t);
            case "$UTCTIME":
                return new TimeRecalledValue(t);
            case "$T":
                return new TimeSpanRecalledValue(respondent?.sessionStartTime);
            case "$J":
                return new DayRecalledValue(tz);
            case "$R":
                return respondent && respondent.dispositionCode
                    ? String(respondent.dispositionCode)
                    : "";
            case "$PHONE":
                return respondent ? respondent.phone || "" : "";
            case "$EMAIL":
                return respondent ? respondent.email || "" : "";
            case "$GEOLOCATION":
                return getCoordinates(respondent);
            case "$RESPONSE_QUALITY_FLAGGED":
                return respondent?.responseQualityFlagged != null
                    ? String(respondent.responseQualityFlagged)
                    : "0";
            case "$RESPONSE_QUALITY_REASON":
                return respondent?.responseQualityReasonCodes || "";
            case "$SURVEY":
                return new SurveyRecalledValue(questionnaire);
            default:
                return new SystemRecalledValue(variableName, variables);
        }
    }

    private static recallQuestionMatch(
        match: any,
        variables: VariablesState,
        navigation: NavigationState,
        questionnaire: QuestionnaireModel,
        respondent?: RespondentModel,
        language?: string,
        mode?: RecallType,
    ): string | RecalledValue {
        const name = this.getNamedGroup(match, "name");
        if (!name) {
            return "";
        }
        const variableName = name.toUpperCase();
        const variable = getVariableWithName(questionnaire, variableName);
        if (mode === RecallType.Text && isEmpty(variable))
            return match[0];
        
        const protectedVariables = questionnaire.settings?.["PROTECTED_VARIABLES"] || [];
        if (protectedVariables.includes(variableName)) {
            return match[0];
        }
        
        const matrix = this.getNamedGroup(match, "matrix");
        const mention = this.getNamedGroup(match, "mention");
        const matrixIndex =
            matrix === undefined
                ? getVariableRow(navigation, questionnaire, variableName)
                : Number(matrix);
        const mentionIndex = !mention ? 0 : Number(mention) - 1;

        const mentions = getMentions(variables, variableName, matrixIndex);

        if (mentions === undefined || mentionIndex >= mentions.length || mentionIndex < 0)
            return null;
        // Check if the question is a date.
        const question = getQuestionWithVariable(questionnaire, variableName);

        if (question != null && mentions.length > 0) {
            switch (question.type) {
                case QuestionType.DateTimeAnswer: {
                    Settings.defaultLocale = respondent.language;
                    const value = mentions[0].value;

                    if (/^-?\d+$/.test(value)) {
                        const format = dateFormat(question);
                        const strippedFormat = format.replace(/[\/\-\.\s:]/g, "");
                        const parsed = DateTime.fromFormat(value, strippedFormat);
                        if (!parsed.isValid) return value;
                        if (getSettingBoolean(question, "Type", "DateTime"))
                            return new DateTimeRecalledValue(parsed);
                        if (getSettingBoolean(question, "Type", "Time"))
                            return new TimeRecalledValue(parsed);
                        return new DateRecalledValue(parsed);
                    }

                    if (getSettingBoolean(question, "Type", "DateTime"))
                        return new DateTimeRecalledValue(
                            DateTime.fromFormat(value, dateFormat(question)),
                        );
                    if (getSettingBoolean(question, "Type", "Time")) {
                        const timeFormat = getSettingString(question, "TimeFormat", "HH:mm");
                        return new TimeRecalledValue(
                            DateTime.fromFormat(value, cDateFormatToLuxon(timeFormat)),
                        );
                    }
                    return new DateRecalledValue(
                        DateTime.fromFormat(value, dateFormat(question)),
                    );
                }
                case QuestionType.FileUpload: {
                    const file = JSON.parse(mentions[0].value);
                    return file ? file.fileName : "";
                }
                default:
                    break;
            }
        }

        return new AnswerRecalledValue(
            mentions,
            respondent.language || language,
            questionnaire,
            variableName,
        );
    }

    private static evaluateRecalledValue(
        match: any,
        recall: () => string | RecalledValue,
        mode: RecallType = RecallType.Code,
    ): string {
        try {
            let value: string | RecalledValue = recall();
            // If mention does not exist, an empty string is returned in text mode (0 in code mode)
            if (!value) return this.getDefaultValue(match, mode);
            const properties = this.getProperties(match);
            if (some(properties, (p) => p.property.toLowerCase() === "text"))
                mode = RecallType.Text;
            while (value instanceof RecalledValue) {
                // Evaluate all properties until a string is returned.
                if (properties.length > 0) {
                    const property = properties.shift();
                    const params = property.param !== undefined ? [property.param] : undefined;
                    value = value.evaluateProperty(mode, property.property.toLowerCase(), params);
                    continue;
                }
                // If the value is still recalled, evaluate it to a string.
                value = value.toString(mode);
            }
            return value === undefined ? "" : value;
        } catch (ex) {
            Log.e(TAG, "RECALL_ERROR", `An error occurred while evaluating "${match}"`, ex);
            return "";
        }
    }

    static identifyVariables = memoize((value: string) => {
        const variables: string[] = [];
        value = XRegExp.replace(
            value,
            SYSTEM_VARIABLE,
            (...args: any[]) => {
                const match = Piping.extractMatchWithGroups(args, SYSTEM_VARIABLE);
                const name = Piping.getNamedGroup(match, "name");
                if (name) {
                    variables.push(name);
                    return name;
                }
                return "";
            },
            "all",
        );
        value = XRegExp.replace(
            value,
            QUESTION_VARIABLE,
            (...args: any[]) => {
                const match = Piping.extractMatchWithGroups(args, QUESTION_VARIABLE);
                const name = Piping.getNamedGroup(match, "name");
                if (name) {
                    variables.push(name);
                    return name;
                }
                return "";
            },
            "all",
        );
        const uniqueVariables = uniq(variables);
        return map(uniqueVariables, toUpper);
    });

    static evaluateVariable(
        interview: InterviewState,
        name: string,
        mode: RecallType = RecallType.Code,
    ): string {
        const match = { name };
        return this.evaluateRecalledValue(
            match,
            () =>
                this.recallQuestionMatch(
                    match,
                    interview.variables,
                    interview.navigation,
                    interview.questionnaire,
                    interview.respondent,
                    interview.language,
                    mode,
                ),
            mode,
        );
    }

    static evaluateRecalls(
        interview: InterviewState,
        value: string,
        mode: RecallType = RecallType.Code,
    ): string {
        if (isNullOrEmpty(value)) return value;
        let recalledValue = value;
        recalledValue = XRegExp.replace(
            recalledValue,
            SYSTEM_VARIABLE,
            (...args: any[]) => {
                try {
                    const match = this.extractMatchWithGroups(args, SYSTEM_VARIABLE);
                    const result = this.evaluateRecalledValue(
                        match,
                        () =>
                            this.recallSystemMatch(
                                match,
                                interview.variables,
                                interview.questionnaire,
                                interview.respondent,
                            ),
                        mode,
                    );
                    if (result !== match[0]) {
                        return this.evaluateRecalls(interview, result, mode);
                    }
                    return result || "";
                } catch (ex) {
                    Log.e(TAG, "REPLACE_ERROR", `Error replacing system match: ${args[0]}`, ex);
                    return "";
                }
            },
            "all",
        );
        recalledValue = XRegExp.replace(
            recalledValue,
            QUESTION_VARIABLE,
            (...args: any[]) => {
                try {
                    const match = this.extractMatchWithGroups(args, QUESTION_VARIABLE);
                    const result = this.evaluateRecalledValue(
                        match,
                        () =>
                            this.recallQuestionMatch(
                                match,
                                interview.variables,
                                interview.navigation,
                                interview.questionnaire,
                                interview.respondent,
                                interview.language,
                                mode,
                            ),
                        mode,
                    );
                    if (result !== match[0]) {
                        return this.evaluateRecalls(interview, result, mode);
                    }
                    return result || "";
                } catch (ex) {
                    Log.e(TAG, "REPLACE_ERROR", `Error replacing match: ${args[0]}`, ex);
                    return "";
                }
            },
            "all",
        );
        return recalledValue;
    }
}
